/**
 * Determines the last non-activity message between store and API messages.
 * @param {Object} messageInStore - The last non-activity message from the store.
 * @param {Object} messageFromAPI - The last non-activity message from the API.
 * @returns {Object} The latest non-activity message.
 */
const getLastNonActivityMessage = (messageInStore, messageFromAPI) => {
  // If both API value and store value for last non activity message
  // are available, then return the latest one.
  if (messageInStore && messageFromAPI) {
    return messageInStore.created_at >= messageFromAPI.created_at
      ? messageInStore
      : messageFromAPI;
  }
  // Otherwise, return whichever is available
  return messageInStore || messageFromAPI;
};

/**
 * Filters out duplicate source messages from an array of messages.
 * @param {Array} messages - The array of messages to filter.
 * @returns {Array} An array of messages without duplicates.
 */
export const filterDuplicateSourceMessages = (messages = []) => {
  const messagesWithoutDuplicates = [];
  // We cannot use Map or any short hand method as it returns the last message with the duplicate ID
  // We should return the message with smaller id when there is a duplicate
  messages.forEach(m1 => {
    if (m1.source_id) {
      const index = messagesWithoutDuplicates.findIndex(
        m2 => m1.source_id === m2.source_id
      );

      if (index < 0) {
        messagesWithoutDuplicates.push(m1);
      }
    } else {
      messagesWithoutDuplicates.push(m1);
    }
  });
  return messagesWithoutDuplicates;
};

/**
 * Retrieves the last message from a conversation, prioritizing non-activity messages.
 * @param {Object} m - The conversation object containing messages.
 * @returns {Object} The last message of the conversation.
 */
export const getLastMessage = m => {
  const lastMessageIncludingActivity = m.messages[m.messages.length - 1];

  const nonActivityMessages = m.messages.filter(
    message => message.message_type !== 2
  );
  const lastNonActivityMessageInStore =
    nonActivityMessages[nonActivityMessages.length - 1];

  const lastNonActivityMessageFromAPI = m.last_non_activity_message;

  // If API value and store value for last non activity message
  // is empty, then return the last activity message
  if (!lastNonActivityMessageInStore && !lastNonActivityMessageFromAPI) {
    return lastMessageIncludingActivity;
  }

  return getLastNonActivityMessage(
    lastNonActivityMessageInStore,
    lastNonActivityMessageFromAPI
  );
};

/**
 * Filters messages that have been read by the agent.
 * @param {Array} messages - The array of messages to filter.
 * @param {number} agentLastSeenAt - The timestamp of when the agent last saw the messages.
 * @returns {Array} An array of read messages.
 */
export const getReadMessages = (messages, agentLastSeenAt) => {
  return messages.filter(
    message =>
      message.message_type !== 0 ||
      message.created_at * 1000 <= agentLastSeenAt * 1000
  );
};

/**
 * Filters messages that have not been read by the agent.
 * @param {Array} messages - The array of messages to filter.
 * @param {number} agentLastSeenAt - The timestamp of when the agent last saw the messages.
 * @returns {Array} An array of unread messages.
 */
export const getUnreadMessages = (messages, agentLastSeenAt) => {
  return messages.filter(
    message =>
      message.message_type === 0 &&
      message.created_at * 1000 > agentLastSeenAt * 1000
  );
};

/**
 * Determines whether an incoming message should increment the unread count for the current user.
 * @param {Object} message - The message being added.
 * @param {number} selectedChatId - The currently open conversation id.
 * @param {number} agentLastSeenAt - The current user's last seen timestamp for the conversation.
 * @returns {boolean}
 */
export const shouldIncrementUnreadCount = (
  message,
  selectedChatId,
  agentLastSeenAt = 0
) => {
  const { conversation_id: conversationId, message_type: messageType } =
    message;

  if (conversationId === selectedChatId || messageType !== 0) {
    return false;
  }

  return message.created_at > agentLastSeenAt;
};

// Pending (and failed) messages have a client-generated UUID id and a client-clock created_at.
const isPendingMessage = message => !Number.isFinite(Number(message.id));

/**
 * Chronological order for chat messages. Websocket events after assignment/automation
 * can arrive out of created_at order; id is only a tiebreaker.
 * Pending messages always stay after sent ones, so client clock skew can't move them.
 */
export const compareMessagesByCreatedAt = (a, b) => {
  const pendingDiff = isPendingMessage(a) - isPendingMessage(b);
  if (pendingDiff) return pendingDiff;

  const timeDiff = Number(a.created_at) - Number(b.created_at);
  if (timeDiff) return timeDiff;

  const idDiff = Number(a.id) - Number(b.id);
  return idDiff || 0;
};

export const sortMessagesByCreatedAt = (messages = []) =>
  [...messages].sort(compareMessagesByCreatedAt);

/**
 * Inserts a message into an already sorted list, in place. Appends in the common case
 * where it is the newest. Returns whether it landed after every sent message.
 */
export const insertMessageByCreatedAt = (messages, message) => {
  let index = messages.length;
  if (index && compareMessagesByCreatedAt(messages[index - 1], message) > 0) {
    let low = 0;
    let high = index - 1;
    while (low < high) {
      const mid = Math.floor((low + high) / 2);
      if (compareMessagesByCreatedAt(messages[mid], message) > 0) high = mid;
      else low = mid + 1;
    }
    index = low;
  }
  messages.splice(index, 0, message);
  return messages.slice(index + 1).every(isPendingMessage);
};
